import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import DashboardHeader from "../../components/Other/DashboardHeader";
import Profile from "../../components/Other/Profile";
import LeagueShell from "./league/LeagueShell";
import { getGameFromSlug } from "../../api/user";
import { toast } from "react-toastify";
import usePullToRefresh from "../../hooks/usePullToRefresh";
import PullToRefreshIndicator from "../../components/Other/PullToRefreshIndicator";
import BackArrowButton from "./landing/BackArrowButton";

// The league stats profile, at BOTH of its addresses.
//
// Client, League Play Dev Request, "League Profile Routing":
//
//   View Record on the podium and clickable leaderboard positions 4 and below
//   must open the trader's league stats profile.
//   When accessed from a standalone leaderboard, the league stats profile must
//   also remain outside the league shell.
//   Reuse the existing league profile component with a standalone viewing mode.
//
// "Reuse … with a standalone viewing mode" is why this is one file and not two.
// The screen is identical either way — same LeagueShell identity block, same
// Profile component, same league scoping. What differs is the ADDRESS it is
// mounted at, and that is what decides whether the app shell is the league's or
// QUSHi's own:
//
//   /<slug>/profile/<id>              in the league (leagueSlugFromPath resolves
//                                     it, so the left rail is the league's nav)
//   /leaderboards/<slug>/profile/<id> standalone (it does not, so the main app
//                                     keeps its rail) — plus a back arrow, since
//                                     there is no league nav to leave by.
//
// See utils/leaguePath.js for why the two URLs behave differently.
const GameProfile = () => {

    const [sidebarToggle] = useOutletContext();
    const { slug, user_id } = useParams();
    const { pathname } = useLocation();
    const standalone = pathname.startsWith("/leaderboards/");
    const [game, setGame] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const refreshFunction = async () => {
        try {
            const response = await getGameFromSlug({ slug });
            const gameData = response?.data?.data;
            if (gameData) {
                setGame(gameData);
                setLoading(true);
            }
        } catch (error) {
            console.error("Error refreshing game profile:", error);
        }
    };

    const { containerProps, isRefreshing, pullDistance } = usePullToRefresh({
        onRefresh: refreshFunction,
        disabled: false,
    });

    useEffect(() => {

        const checkGameDates = (start_date, end_date) => {

            const endDate = new Date(end_date.replace(" ", "T")); // Parse game.end_date
            const startDate = new Date(start_date.replace(" ", "T")); // Parse game.start_date
            const today = new Date(); // Get today's date
            today.setHours(0, 0, 0, 0); // Set time to midnight to ignore time component

            if (today >= startDate && today <= endDate) {
                return true;
            } else {
                return false;
            }

        }

        const fetchGameId = async (slug) => {
            try {
                const response = await getGameFromSlug({ slug });
                const gameData = response?.data?.data;

                if (gameData) {

                    if (checkGameDates(gameData.start_date, gameData.end_date)) {
                        setGame(gameData);
                        setLoading(true);
                    } else {
                        setGame(gameData);
                        setLoading(true);
                        // toast.error("League is not active");
                        // navigate("/contest/my-games");
                        // navigate("/contest/my-games", { state: { viewMode: "member" } });
                    }
                } else {
                //    toast.error("League not found");
                    // navigate("/contest/my-games");
                  //  navigate("/contest/my-games", { state: { viewMode: "member" } });
                }

            } catch (error) {
                console.error("Error fetching game ID:", error);
            }
        };

        fetchGameId(slug);

    }, [slug]);

    return (<>
        <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
        <div {...containerProps}>
            {/* `{...(game && { game })}` — the same call the other league screens
                make, so the header knows which league it is standing in. */}
            <DashboardHeader toggle={sidebarToggle} {...(game && { game })} />
            {/* Client League Play Profiles spec, item 2: "Competition eyebrow
                and the dynamic league title above the profile, no container,
                left aligned to the bio card."
                `compact standalone` is the shell's eyebrow-plus-name mode —
                the identical one Leaderboard, Watchlist and Portfolio already
                use — so the two lines above the profile are the same
                component the rest of the league uses, not a copy of it.
                Profile.jsx drops its own header/container when game_id is set
                (see `Shell` there), so nothing doubles up. */}
            {
                loading && (
                    <LeagueShell
                        game={game}
                        active="profile"
                        compact
                        standalone
                        /* "The back arrow must return the user to the screen
                           they came from." Only on the standalone address: a
                           member inside the league already has the league's own
                           nav to leave by, and the client did not ask for a
                           second way out of a screen that has one.

                           `leading`, not a child: a back arrow belongs ABOVE the
                           eyebrow and the league name, and children render below
                           them. */
                        leading={standalone ? <BackArrowButton /> : null}
                    >
                        <Profile
                            game_id={game.id}
                            slug={slug}
                            profile_id={user_id}
                            /* The whole league record, not just its id. The wording
                               depends on the COMPETITION TYPE (Public League /
                               Private League / Cash Game) and the locked state needs
                               private_portfolio and end_date — none of which can be
                               recovered from game_id alone. Already in hand here, so
                               it costs no extra request. */
                            game={game}
                        />
                    </LeagueShell>
                )
            }
        </div>
    </>
    );
};

export default GameProfile;